import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';

import { ErrorSeverity } from 'later/services/errors';

import type RouterService from '@ember/routing/router-service';
import type StoreService from '@ember-data/store';
import type UserAgentService from 'ember-useragent/services/user-agent';
import type GroupModel from 'later/models/group';
import type SessionService from 'later/services/-private/session';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type ErrorsService from 'later/services/errors';
import type FayeService from 'later/services/faye';
import type LocalStorageManagerService from 'later/services/local-storage-manager';
import type NotificationsService from 'later/services/notifications';
import type PaymentService from 'later/services/payment';
import type { Maybe, ModelFrom, Transition } from 'shared/types';

export default class ClusterRoute extends Route {
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service declare errors: ErrorsService;
  @service declare faye: FayeService;
  @service declare localStorageManager: LocalStorageManagerService;
  @service declare payment: PaymentService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare userAgent: UserAgentService;
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;

  beforeModel(transition: Transition): void {
    this.session.requireAuthentication(transition, 'user.login');
  }

  model(params: { profile_path: string }): Maybe<GroupModel> {
    return this.store.peekAll('group').findBy('slug', params.profile_path);
  }

  async afterModel(group: ModelFrom<ClusterRoute>, transition: Transition): Promise<void> {
    if (!this.auth.currentAccount) {
      this.auth.logout.perform();
      return;
    }

    // NOTE: If the mobile user is not on an allowed page, redirect to "/mobile"
    const allowedMobilePages = ['linkinbio', 'unsubscribe', 'partnerships'];
    if (this.userAgent.device.isMobile && !allowedMobilePages.some((route) => transition.to.name.includes(route))) {
      const queryParams = transition.from ? transition.from.queryParams : {};
      const { utm_source, utm_medium, utm_campaign } = queryParams;
      this.router.replaceWith('mobile', { queryParams: { utm_source, utm_medium, utm_campaign } });
    }
    if (isNone(group)) {
      const defaultGroup = this.store.peekAll('group').get('firstObject');

      if (isNone(defaultGroup)) {
        // Temporary log to track down cause of redirecting to the `nogroup` route
        try {
          this.errors.log(
            'No group found, cluster route',
            {
              localGroupIds: this.store.peekAll('group').mapBy('id'),
              lastGroupId: this.cache.retrieve('last_group_id') || 'none',
              localStorage: { ...window.localStorage }
            },
            ErrorSeverity.Info
          );
        } catch (error) {
          this.errors.log('Could not log nogroup error, cluster route', { error }, ErrorSeverity.Info);
        }

        this.router.replaceWith('nogroup');
      } else {
        this.router.replaceWith('/' + defaultGroup.get('slug'));
      }
    } else {
      this.cache.add('last_group_id', group.id, { expiry: this.cache.expiry(1, 'day'), persist: true });
      this.auth.setGroup(group);

      //preload savedCaptions and labels async -iMack
      group.get('savedCaptions');
      group.get('labels');
      // on success
      this.faye.subscribeGroup(group);

      if (this.auth.currentAccount.canUseNotificationCenter) {
        try {
          await this.notifications.setup.perform(group);
        } catch (error) {
          this.errors.log('Unable to set up notification center', { error }, ErrorSeverity.Error);
        }
      }
    }

    if (this.payment.hasUpcomingExpiringPaymentMethod) {
      this.payment.alertPaymentExpiry();
    }
  }

  setupController(): void {
    this.localStorageManager.removeItem('show_nav_large');
    this.localStorageManager.removeItem('show_nav_small');
  }

  deactivate(): void {
    const group = this.modelFor('cluster') as GroupModel;
    this.faye.unsubscribeGroup(group);
  }
}
